import { cn, numberToCurrency } from '@/utils'
import type { NotCounted } from '../model/portfolioOverview'
import type { ActivityTotals, DateWindow } from '../model/types'
import { dateRangeText } from '../portfolioText'
import { LedgerIncompleteMark } from './LedgerIncompleteMark'
import {
  leftOutSentence,
  type MoneyRow,
  moneyColumn,
  notCountedSentence,
} from './moneyText'

interface MoneyInOutProps {
  window: DateWindow
  totals: ActivityTotals
  notCounted: NotCounted[]
  accountCount: number
  isLedgerIncomplete: boolean
}

const MoneyRowLine = ({ row }: { row: MoneyRow }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt
      className={cn('min-w-0', row.isTotal ? 'text-blue-7' : 'text-ink-subtle')}
    >
      {row.label}
      {row.detail && (
        <span className="mt-px block text-xs text-ink-subtle">
          {row.detail}
        </span>
      )}
    </dt>
    <dd className="m-0 whitespace-nowrap text-right tabular-nums">
      {row.amount == null ? (
        <span className="text-ink-subtle">None found</span>
      ) : (
        numberToCurrency(row.amount)
      )}
    </dd>
  </div>
)

// Money in and out over the same dates as the change, from both the activity and the cash ledger.
export const MoneyInOut = ({
  window,
  totals,
  notCounted,
  accountCount,
  isLedgerIncomplete,
}: MoneyInOutProps) => {
  const column = moneyColumn(totals)
  const notCountedText = notCountedSentence(notCounted)

  return (
    <div>
      <div className="text-xs text-ink-subtle">
        Money in and out, {dateRangeText(window)}
        {isLedgerIncomplete && (
          <>
            {' '}
            <LedgerIncompleteMark />
          </>
        )}
      </div>
      <p className="mt-0.5 text-xs text-ink-subtle">
        {leftOutSentence(totals, accountCount)}
      </p>
      {notCountedText && (
        <p className="mt-1.5 text-xs text-ink-subtle">{notCountedText}</p>
      )}
      {column.noneSentence && <p className="mt-4">{column.noneSentence}</p>}
      <dl className="mt-4 flex flex-col gap-2.5">
        {column.movements.map((row) => (
          <MoneyRowLine key={row.label} row={row} />
        ))}
        {column.movements.length > 0 && <div className="h-1.5" />}
        {column.income.map((row) => (
          <MoneyRowLine key={row.label} row={row} />
        ))}
      </dl>
    </div>
  )
}
